import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

// ---- Button ----
export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
}) {
  const base = 'inline-flex items-center justify-center font-medium rounded-md transition focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  const variants: Record<string, string> = {
    primary: 'bg-forest-700 text-white hover:bg-forest-800 focus:ring-forest-600',
    secondary: 'bg-paper border border-line text-soft hover:bg-cream focus:ring-forest-600',
    ghost: 'text-soft hover:bg-cream focus:ring-forest-600',
    danger: 'bg-rose-600 text-white hover:bg-rose-700 focus:ring-rose-500',
  };
  const sizes: Record<string, string> = {
    sm: 'px-2.5 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };
  return (
    <button className={`${base} ${variants[variant]} ${sizes[size]} ${className}`} {...props}>
      {children}
    </button>
  );
}

// ---- Input ----
export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded-md border border-line px-3 py-2 text-sm shadow-sm focus:border-forest-600 focus:outline-none focus:ring-1 focus:ring-forest-600 ${props.className ?? ''}`}
    />
  );
}

// ---- Textarea ----
export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={`w-full rounded-md border border-line px-3 py-2 text-sm shadow-sm focus:border-forest-600 focus:outline-none focus:ring-1 focus:ring-forest-600 ${props.className ?? ''}`}
    />
  );
}

// ---- Select ----
export function Select({ children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={`w-full rounded-md border border-line px-3 py-2 text-sm shadow-sm focus:border-forest-600 focus:outline-none focus:ring-1 focus:ring-forest-600 bg-paper ${props.className ?? ''}`}
    >
      {children}
    </select>
  );
}

// ---- Card ----
export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-line bg-paper shadow-sm ${className}`}>
      {children}
    </div>
  );
}

// ---- Badge ----
export function Badge({
  children,
  variant = 'gray',
  className = '',
}: {
  children: ReactNode;
  variant?: 'gray' | 'green' | 'amber' | 'red' | 'blue' | 'indigo' | 'purple';
  className?: string;
}) {
  const variants: Record<string, string> = {
    gray: 'bg-cream text-soft',
    green: 'bg-green-100 text-green-800',
    amber: 'bg-amber-100 text-amber-800',
    red: 'bg-rose-100 text-rose-800',
    blue: 'bg-sky-100 text-sky-800',
    indigo: 'bg-forest-100 text-forest-800',
    purple: 'bg-purple-100 text-purple-800',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
}

// ---- Label ----
export function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-soft mb-1">
      {children}
    </label>
  );
}

// ---- Helper text ----
export function HelperText({ children }: { children: ReactNode }) {
  return <p className="text-xs text-soft mt-1">{children}</p>;
}
